import { selectAll } from "@fruugo/utilities";
import { ProductTag } from "./components/ProductTag";
import { Icon } from "./components/Icon";
import { i18n } from "./i18n";
import {
  ProductTagContent,
  ProductTagType,
  ProductTagTypeObject,
} from "../types";
import { sendClickEvent } from "./analytics/google-analytics";

type TagTimerState = {
  timeoutId: ReturnType<typeof setTimeout> | null;
  remaining: number;
  lastStart: number;
  index: number;
};

declare let window: ProductPage;

const DISPLAY_DELAY_DURATION = 3000;
const FADE_DURATION = 5000;
const TRANSITION_DURATION = 500;
const SLIDE_UP_CLASS = "product-tags--slide-up";
const SLIDE_DOWN_CLASS = "product-tags--slide-down";
const SHOW_CLASS = "product-tag--show";
const productTagsData = window.productTags;

export function initProductTags() {
  const tagContainers = selectAll<HTMLElement>(".js-product-tags");
  if (!tagContainers || !productTagsData?.length) return;

  // Having to use a Map instead of an object due to tagContainers being an array.
  const tagTimerState = new Map<HTMLElement, TagTimerState>();

  const getState = (element: HTMLElement) => {
    const state = tagTimerState.get(element);
    return state;
  };

  const setState = (element: HTMLElement, state?: TagTimerState) => {
    if (!state) {
      state = {
        timeoutId: null,
        remaining: FADE_DURATION,
        lastStart: 0,
        index: 0,
      };
    }

    tagTimerState.set(element, state);
  };

  const constructTagContent = (type: ProductTagType, values: string[]) => {
    let content: ProductTagContent | null;

    switch (type) {
      case ProductTagTypeObject.FreeShipping:
        content = {
          title: `${i18n("product.product-tags.free-shipping.title")}`,
          description: `${i18n(
            "product.product-tags.free-shipping.description"
          )}`,
          icon: {
            name: "redeem",
            fill: "#008055",
          },
        };
        break;
      case ProductTagTypeObject.ExpressShipping:
        content = {
          title: `${i18n("product.product-tags.express-shipping.title")}`,
          description: `${i18n(
            "product.product-tags.express-shipping.description"
          )}`,
          icon: {
            name: "delivery-truck-speed",
            fill: "#007CBB",
          },
        };
        break;
      case ProductTagTypeObject.LowStock:
        content = {
          title: `${i18n("product.product-tags.low-stock.title")}`,
          description: `${
            values[0] === "1"
              ? i18n("product.product-tags.low-stock.description.one-item")
              : i18n("product.product-tags.low-stock.description", values[0])
          }`,
          icon: {
            name: "timer",
            fill: "#6F42C1",
          },
        };
        break;
      default:
        content = null;
    }

    return content;
  };

  tagContainers.forEach((element) => {
    element.appendChild(
      <div
        className="product-tags__wrapper d-flex align-items-center"
        onMouseEnter={() => pauseTags(element)}
        onMouseLeave={() => resumeTags(element)}
      >
        {productTagsData.map((item) => {
          const tagContent = constructTagContent(
            item.productTagType,
            item.values
          );

          if (!tagContent) return;
          return (
            <ProductTag
              title={tagContent.title}
              description={tagContent.description}
              icon={tagContent.icon}
            />
          );
        })}
        <button
          className="product-tag__close border-0"
          onClick={() => hideTags()}
          aria-label={i18n("product.product-tags.aria-label.close-tag")}
        >
          <Icon name="close" className="icon small" fill="#ffffff" />
        </button>
      </div>
    );
  });

  const triggerTags = (element: HTMLElement) => {
    const state = getState(element);
    if (!state) {
      setState(element);
    } else {
      state.index = 0;
      state.remaining = FADE_DURATION;
      setState(element, state);
    }

    showTags(element);
  };

  setTimeout(() => {
    tagContainers.forEach((element) => {
      element.classList.add(SLIDE_UP_CLASS);
      triggerTags(element);
    });
  }, DISPLAY_DELAY_DURATION);

  const hideTags = () => {
    sendClickEvent("pdp_product_tags", "Close Tags");
    tagContainers.forEach((element) => {
      element.classList.add(SLIDE_DOWN_CLASS);
      // Using setTimeout over transitionend as this has to work on desktop and mobile at the same time.
      setTimeout(() => {
        element.classList.remove(SLIDE_DOWN_CLASS, SLIDE_UP_CLASS);
        element.innerHTML = "";
      }, TRANSITION_DURATION);
    });
  };

  const pauseTags = (element: HTMLElement) => {
    const state = getState(element);
    if (!state) {
      setState(element);
    } else {
      if (!state.timeoutId) return;
      clearTimeout(state.timeoutId);
      state.timeoutId = null;
      state.remaining = Math.max(
        0,
        state.remaining - (Date.now() - state.lastStart)
      );
      setState(element, state);
    }
  };

  const resumeTags = (element: HTMLElement) => {
    const state = getState(element);
    if (!state) {
      setState(element);
    } else {
      state.lastStart = Date.now();
      const delay = state.remaining;
      state.timeoutId = setTimeout(() => {
        state.remaining = FADE_DURATION;
        setState(element, state);
        showTags(element);
      }, delay);
    }
  };

  function showTags(element: HTMLElement) {
    const state = getState(element);
    const productTags = Array.from(element.querySelectorAll(".product-tag"));
    const activeTag = productTags.find((el) =>
      el.classList.contains(SHOW_CLASS)
    );

    if (activeTag) {
      activeTag.classList.remove(SHOW_CLASS);
    }

    if (!state) {
      setState(element);
    } else {
      const nextElement = productTags[state.index];

      if (nextElement) {
        nextElement.classList.add(SHOW_CLASS);
      }

      state.index = (state.index + 1) % productTags.length;
      state.lastStart = Date.now();
      state.remaining = FADE_DURATION;
      state.timeoutId = setTimeout(() => showTags(element), FADE_DURATION);
      setState(element, state);
    }
  }
}
